import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as dashboardApi from '../../services/api/dashboardApi';

// ─── Async Thunks ────────────────────────────────────────────────────────────

export const fetchKpis = createAsyncThunk(
  'dashboard/fetchKpis',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getKpis();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch KPIs' }
      );
    }
  }
);

export const fetchRecentActivity = createAsyncThunk(
  'dashboard/fetchRecentActivity',
  async (params, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getRecentActivity(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch activity' }
      );
    }
  }
);

export const fetchHealthSummary = createAsyncThunk(
  'dashboard/fetchHealthSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getHealthSummary();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to fetch health summary' }
      );
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  kpis: {
    totalTenants: 0,
    activeTenants: 0,
    suspendedTenants: 0,
    provisioningTenants: 0,
    maintenanceTenants: 0,
    offboardingTenants: 0,
    avgHealthScore: 0,
    monthlyRevenue: 0,
    lastUpdated: null,
  },
  kpisLoading: false,
  kpisError: null,

  recentActivity: [],
  activityLoading: false,
  activityError: null,

  healthSummary: {
    healthy: 0,
    warning: 0,
    critical: 0,
    unknown: 0,
    activeIncidents: 0,
    slaBreaches: 0,
    lastChecked: null,
  },
  healthLoading: false,
  healthError: null,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    resetDashboard(state) {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ── KPIs ──
    builder
      .addCase(fetchKpis.pending, (state) => {
        state.kpisLoading = true;
        state.kpisError = null;
      })
      .addCase(fetchKpis.fulfilled, (state, action) => {
        state.kpisLoading = false;
        state.kpis = action.payload;
      })
      .addCase(fetchKpis.rejected, (state, action) => {
        state.kpisLoading = false;
        state.kpisError = action.payload?.message || 'Failed to fetch KPIs';
      });

    // ── Recent Activity ──
    builder
      .addCase(fetchRecentActivity.pending, (state) => {
        state.activityLoading = true;
        state.activityError = null;
      })
      .addCase(fetchRecentActivity.fulfilled, (state, action) => {
        state.activityLoading = false;
        state.recentActivity = action.payload;
      })
      .addCase(fetchRecentActivity.rejected, (state, action) => {
        state.activityLoading = false;
        state.activityError = action.payload?.message || 'Failed to fetch activity';
      });

    // ── Health Summary ──
    builder
      .addCase(fetchHealthSummary.pending, (state) => {
        state.healthLoading = true;
        state.healthError = null;
      })
      .addCase(fetchHealthSummary.fulfilled, (state, action) => {
        state.healthLoading = false;
        state.healthSummary = action.payload;
      })
      .addCase(fetchHealthSummary.rejected, (state, action) => {
        state.healthLoading = false;
        state.healthError = action.payload?.message || 'Failed to fetch health summary';
      });
  },
});

export const { resetDashboard } = dashboardSlice.actions;

export default dashboardSlice.reducer;
